import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CardActionArea from '@mui/material/CardActionArea';
import Box from '@mui/material/Box';
import { useTranslation } from 'next-i18next';

import Typography from 'src/components/ui/Typography';
import Link from 'src/components/ui/Link';
import formatPrice from 'src/utils/formatPrice';
import { colours } from 'src/constants/colours';
import { noImagePath } from 'src/constants/defaultImages';
import type { Ad } from 'src/types/__generated__/graphql';
import { AdStatuses } from 'src/types/__generated__/graphql';
import { routePathAd } from 'src/constants/router';
import { styledComponent } from 'src/utils/styled';
import TooltipButton from 'src/components/ui/TooltipButton';
import ToggleItemInGallery from 'src/components/ui/ToggleItemInGallery';
import Tag from 'src/components/ui/Tag';
import PriceEvaluationTag from 'src/components/ui/PriceEvaluationTag';
import HomeAttributeIconBar, { HomeAttributeIconBarVariants } from 'src/components/ad/HomeAttributeIconBar';
import { AnalyticsEvent, EventParamKeys } from 'src/utils/analytics';
import useDynamicLogEvents, { AnalyticsContextProvider } from 'src/hooks/useDynamicLogEvents';

const StyledCard = styledComponent(Card)`
    pointer-events: all;
    width: 20.5rem;
    border-radius: 1rem;
`;

const StyledCardActionArea = styledComponent(CardActionArea)<{ href: string }>`
    display: flex;
    align-items: stretch;
    width: 20.5rem;
    border-radius: 1rem;
    justify-content: flex-start;
`;

const StyledCardMedia = styledComponent(CardMedia)<{
    $status?: AdStatuses | null;
    $isDarkCard: boolean;
}>`
    min-width: 6rem;
    ${({ $isDarkCard }) =>
        $isDarkCard ? `background-color: ${colours.white.O04};` : `background-color: ${colours.white.main};`};
    ${({ $status }) =>
        $status !== AdStatuses.Available &&
        `
        opacity: 0.5;
    `}
`;

const StyledCardContent = styledComponent(CardContent)`
    min-width: 10rem;
    padding: 0.5rem 0.75rem !important;
    position: relative;
    width: 100%;
    margin-right: -0.5rem;
`;

interface Props {
    ad: Partial<Ad>;
    isDarkCard?: boolean;
}

const SearchAdsPageMarkerCard: React.FC<Props> = ({ ad, isDarkCard }) => {
    const { t, i18n } = useTranslation(['common', 'ad']);
    const { logEventDynamic } = useDynamicLogEvents();

    const CardLink = useMemo(
        () =>
            ({ children }: PropsWithChildren) =>
                (ad.id && (ad.status === 'available' || ad.status === 'ended') ? (
                    <StyledCardActionArea
                        LinkComponent={Link}
                        href={`${routePathAd}/${ad.id}`}
                        name="resultMapViewCard"
                        onClick={() => {
                            logEventDynamic(AnalyticsEvent.DetailsFromMap);
                        }}
                        disableRipple
                    >
                        {children}
                    </StyledCardActionArea>
                ) : (
                    children
                )) as React.ReactElement,
        [ad.id, ad.status, logEventDynamic],
    );

    return (
        <AnalyticsContextProvider
            value={{
                [EventParamKeys.ItemId]: ad.id,
                [EventParamKeys.ItemName]: ad.title ?? undefined,
                [EventParamKeys.PropertyType]: ad.propertyType,
            }}
        >
            <StyledCard>
                <CardLink>
                    {ad.media && (
                        <StyledCardMedia
                            image={String(ad.media[0]?.url ?? noImagePath)}
                            title={ad.title as string}
                            $status={ad.status}
                            $isDarkCard={Boolean(isDarkCard)}
                        />
                    )}
                    <StyledCardContent>
                        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h3">
                                {ad.type === 'sell'
                                    ? formatPrice({
                                          price: Number(ad.price),
                                          lang: i18n.language,
                                      })
                                    : t('ad:rentPrice', {
                                          rentPrice: formatPrice({
                                              price: Number(ad.rent),
                                              lang: i18n.language,
                                          }),
                                      })}
                            </Typography>
                            <Box
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                                padding="0"
                                ml="0.75rem"
                            >
                                <TooltipButton
                                    title={t('common:tooltips.addToGalleries')}
                                    ButtonComponent={ToggleItemInGallery}
                                    buttonProps={{
                                        ad,
                                    }}
                                    isControlled
                                />
                            </Box>
                        </Box>
                        <Box mr="1rem" mt="-0.35rem">
                            <Typography
                                data-target="card-address"
                                data-target-id="card-address"
                                variant="body2"
                                color="textSecondary"
                                noWrap
                            >{`${ad.address?.city?.name ?? ad.address?.cityName}${
                                ad.address?.zipCode ? `, ${ad.address.zipCode} ` : ''
                            }`}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" mt="-0.25rem">
                            <Typography variant="body2" color="textSecondary" noWrap>
                                {t(`ad:propertyTypes.${ad.propertyType}`)}
                            </Typography>
                            <Typography pl={0.5} pr={0.5}>
                                ·
                            </Typography>
                            <Box my={0.5} display="flex" alignItems="center">
                                <HomeAttributeIconBar
                                    ad={ad}
                                    homePropertiesDensity={HomeAttributeIconBarVariants.SMALL}
                                />
                            </Box>
                        </Box>
                        <Box display="flex" flexDirection="row" alignItems="flex-end" height="fit-content" mb="0.25rem">
                            {ad.type === 'sell' && ad.price && ad.homadataEvaluation && (
                                <PriceEvaluationTag
                                    price={ad.price}
                                    confidenceMin={ad.homadataEvaluation.confidenceMin}
                                    confidenceMax={ad.homadataEvaluation.confidenceMax}
                                    size="tiny"
                                />
                            )}
                            {ad.isNew && (
                                <Box>
                                    <Tag
                                        backgroundColor={colours.tertiaryOpaque.O12}
                                        textColor={colours.tertiary.main}
                                        label={t('ad:new')}
                                        size="tiny"
                                    />
                                </Box>
                            )}
                        </Box>
                    </StyledCardContent>
                </CardLink>
            </StyledCard>
        </AnalyticsContextProvider>
    );
};

export default SearchAdsPageMarkerCard;
